import SubtypeToolbox from "./SubtypeToolbox";
import SubtypePreview from "./SubtypePreview";
import { useCallback, useEffect } from "react";
import SubtypePropertyEditor from "./SubtypePropertyEditor";
import { Stack, Typography } from "@mui/material";
import { ActionModuleSubtypesState } from "../../../../types/actionModule/editor/actionModuleEditorTypes";
import { SubtypeState } from "../../../../types/actionModule/editor/subtypeEditorTypes";
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import { isDropAreaDropData, isNewSubtypeDragData, isSubtypeDragData } from "./dragAndDropHelpers";
import {
    monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import {
    type Edge,
    extractClosestEdge,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { SubtypeDefinition } from "../../../../types/actionModule/actionModule";
import { useTranslation } from "react-i18next";


interface Props {
    value: ActionModuleSubtypesState;
    onChanged: (value: ActionModuleSubtypesState) => void;
}

export default function SubtypeEditor(props: Props) {
    const { t } = useTranslation();

    const reorderItem = useCallback(
        ({
            startIndex,
            indexOfTarget,
            closestEdgeOfTarget,
        }: {
            startIndex: number;
            indexOfTarget: number;
            closestEdgeOfTarget: Edge | null;
        }) => {
            const finishIndex = getReorderDestinationIndex({
                startIndex,
                closestEdgeOfTarget,
                indexOfTarget,
                axis: 'vertical',
            });

            if (finishIndex === startIndex) {
                return;
            }

            const reordered = reorder({
                list: props.value.subtypes,
                startIndex,
                finishIndex,
            });

            for (let i = 0; i < reordered.length; i++) {
                reordered[i].index = i;
            }

            props.onChanged({
                ...props.value.subtypes,
                subtypes: reordered,
            });
        },
        [props.value.subtypes],
    );

    const insertSubtype = useCallback((state: SubtypeDefinition, index: number) => {
        const newSubtypes = [...props.value.subtypes];

        const newEditorId = props.value.subtypes
            .map(s => s.editorId)
            .reduce((p, c) => Math.max(p, c), 0) + 1;

        const properties = {};
        for(const property of state.editableProperties) {
            properties[property.name] = property.defaultValue;
        }

        const newSubtype = {
            definition: state,
            subtype: {
                id: null,
                name: "",
                isRequiredForCompletionByCompanyManagement: false,
                isRequiredForCompletionByEmployee: false,
            },
            properties: properties,
            index: 0,
            editorId: newEditorId
        };

        newSubtypes.splice(index, 0, newSubtype);

        for (let i = 0; i < newSubtypes.length; i++) {
            newSubtypes[i].index = i;
        }

        props.onChanged({
            ...props.value,
            subtypes: newSubtypes
        });
    }, [props.value]);

    const selectSubtype = useCallback((s: SubtypeState) => {
        props.onChanged({
            ...props.value,
            selectedSubtype: s
        })
    }, [props.value]);

    const deleteSubtype = useCallback((s: SubtypeState) => {
        props.onChanged({
            ...props.value,
            subtypes: props.value.subtypes.filter(x => x !== s)
        })
    }, [props.value])

    const onSubtypeChange = useCallback((oldState: SubtypeState, newState: SubtypeState) => {
        props.onChanged({
            ...props.value,
            subtypes: props.value.subtypes.map((s) => s === oldState
                ? newState
                : s
            )
        });
    }, [props.value]);

    useEffect(() => monitorForElements({
        canMonitor({ source }) {
            return true;
        },
        onDrop({ location, source }) {
            const target = location.current.dropTargets[0];
            if (!target) {
                return;
            }

            const targetData = target.data;
            const sourceData = source.data;

            if (isSubtypeDragData(targetData)) {
                const indexOfTarget = props.value.subtypes.findIndex(
                    (item) => item.editorId === targetData.editorId
                );
                if (indexOfTarget < 0) {
                    return;
                }

                const closestEdgeOfTarget = extractClosestEdge(targetData);

                if (isSubtypeDragData(sourceData)) {
                    reorderItem({
                        startIndex: sourceData.index,
                        indexOfTarget,
                        closestEdgeOfTarget,
                    });
                }
                else if (isNewSubtypeDragData(sourceData)) {
                    const insertIndex = closestEdgeOfTarget === "bottom"
                        ? indexOfTarget + 1
                        : indexOfTarget;

                    insertSubtype(
                        sourceData.subtype,
                        insertIndex,
                    )
                }
            } else if (isDropAreaDropData(targetData) && isNewSubtypeDragData(sourceData)) {
                insertSubtype(sourceData.subtype, 0)
            }
        }
    }));

    return <div style={{
        display: "flex",
        flexDirection: "row",
        gap: "10px",
        height: "100%",
        alignContent: "stretch",
        alignItems: "stretch",
        background: "white"
    }}>
        <div style={{ flex: 1, }}>
            <Stack direction="column" sx={{
                p: 2,
                height: "100%",
                boxSizing: "border-box"
            }}>
                <SubtypeToolbox />
            </Stack>
        </div>

        <div style={{
            minWidth: 0,
            flex: 1,
            paddingTop: "20px",
            paddingBottom: "20px",
        }}>
            <SubtypePreview state={props.value.subtypes}
                onSelectSubtype={selectSubtype}
                onDeleteSubtype={deleteSubtype}
                editorState={props.value} />
        </div>

        <div style={{
            flex: 1,
            padding: "10px",
        }}>
            <Stack direction="column" sx={{
                p: 2,
                height: "100%",
                boxSizing: "border-box",
            }}>
                <Typography variant="overline">{t("actionModuleEditor.tabSubtypes.settings")}</Typography>
                <SubtypePropertyEditor
                    selectedSubtype={props.value.selectedSubtype}
                    onChange={onSubtypeChange} />
            </Stack>
        </div>
    </div>
}